import React, { useEffect } from 'react';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import Dashboard from './pages/Dashboard';
import DevicePage from './pages/DevicePage';
import LoginPage from './pages/LoginPage';
import UsersAdminPage from './pages/UsersAdminPage';
import { AuthProvider } from './contexts/AuthContext';
import { DevicesProviderWithLogging } from './contexts/DevicesContext';
import { SteeringProvider } from './contexts/SteeringContext';
import { LoggingProvider, useLogging } from './contexts/LoggingContext';

// Define our new modern color scheme
const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#3A86FF',
            light: '#5D9AFF',
            dark: '#2A6DE0',
        },
        secondary: {
            main: '#FF006E',
            light: '#FF4D94',
            dark: '#C8005A',
        },
        success: {
            main: '#38B000',
            light: '#4BD400',
            dark: '#2A8C00',
        },
        warning: {
            main: '#FFBE0B',
            light: '#FFCE4D',
            dark: '#D99F00',
        },
        error: {
            main: '#FF5C8D',
            light: '#FF7BA7',
            dark: '#D14272',
        },
        background: {
            default: '#151C26',
            paper: '#1E2A3A',
        },
        text: {
            primary: '#ffffff',
            secondary: 'rgba(255, 255, 255, 0.7)',
        },
    },
    typography: {
        fontFamily: 'Inter, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        h1: {
            fontSize: '2.2rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
        },
        h2: {
            fontSize: '1.8rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h4: {
            fontSize: '1.3rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1.1rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
        },
        subtitle1: {
            fontSize: '0.95rem',
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '0.85rem',
            fontWeight: 500,
        },
        body1: {
            fontSize: '0.95rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.85rem',
            lineHeight: 1.5,
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: '#1E2A3A', 
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(0, 0, 0, 0.3)',
                        borderColor: 'rgba(58, 134, 255, 0.3)',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '24px',
                    padding: '8px 16px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                },
                contained: {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        transform: 'translateY(-1px)',
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(90deg, #3A86FF 0%, #4D94FF 100%)',
                    '&:hover': {
                        background: 'linear-gradient(90deg, #2A6DE0 0%, #3A86FF 100%)',
                    },
                },
                containedSecondary: {
                    background: 'linear-gradient(90deg, #FF006E 0%, #FF3B8C 100%)',
                    '&:hover': {
                        background: 'linear-gradient(90deg, #D60063 0%, #FF006E 100%)',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    fontWeight: 500,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: '#1E2A3A',
                    borderRadius: '16px',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(58, 134, 255, 0.15)',
                        color: '#3A86FF',
                        fontWeight: 500,
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(58, 134, 255, 0.25)',
                    },
                },
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        transform: 'translateY(-1px)',
                    },
                },
            },
        },
    },
});

// ErrorHandler component to catch and log errors
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // We'll use logging context in a separate component since we can't use hooks here
    console.error(`Error boundary caught: ${error.message}`, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'red' }}>
          <h2>Something went wrong.</h2>
          <p>The error has been logged. Please refresh the page.</p>
          <LogErrorToContext error={this.state.error} />
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to log errors using the context
const LogErrorToContext: React.FC<{ error?: Error }> = ({ error }) => {
  const { addLog } = useLogging();
  
  useEffect(() => {
    if (error) {
      addLog({
        message: `Application Error: ${error.message}`,
        level: 'error',
        source: 'ui',
        userName: 'System'
      });
    }
  }, [error, addLog]);
  
  return null;
};

function AuthenticatedShell() {
    return (
        <DevicesProviderWithLogging>
            <SteeringProvider>
                <Layout>
                    <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/device/:id" element={<DevicePage />} />
                        <Route
                            path="/admin/users"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <UsersAdminPage />
                                </PrivateRoute>
                            }
                        />
                    </Routes>
                </Layout>
            </SteeringProvider>
        </DevicesProviderWithLogging>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <LoggingProvider>
                <ErrorBoundary>
                    <Router>
                        <AuthProvider>
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />
                                <Route
                                    path="/*"
                                    element={
                                        <PrivateRoute>
                                            <AuthenticatedShell />
                                        </PrivateRoute>
                                    }
                                />
                            </Routes>
                        </AuthProvider>
                    </Router>
                </ErrorBoundary>
            </LoggingProvider>
        </ThemeProvider>
    );
}

export default App;
